/**
 * Does it fit? The plan's advisory checks on placed radiators and the outdoor
 * unit, as pure functions of plan metres (tests: `plan-fit.test.ts`).
 *
 * Asked for on 2026-09-10: "If the wall size is not enough, it's a small space,
 * and I select the very long radiator, then it should show me something like a
 * note [not] fitting in this area ... so then I can choose another size or
 * another wall."
 *
 * WHAT IS CHECKED, AND AGAINST WHAT
 *
 *   Radiator against a wall (snapped, or free but with its back within its own
 *   depth of a wall and parallel to it within 5°):
 *     - the CLEAR RUN where it sits: the wall's length — joined with any
 *       collinear piece RoomPlan split it into — less every door and opening on
 *       it. Longer than the clear run: "too long". Fits, but hangs past the
 *       wall's end: "overhangs the corner".
 *     - any door or opening on that wall it runs across: "blocks the door".
 *     - any window on that wall it runs under: its top (mounting height +
 *       height) against the window's sill, measured from the room's floor
 *       (`PlanFloorLevel` in @sts/geometry). A radiator under a window is
 *       normal and is never flagged for that alone. Where the capture does not
 *       settle the floor, the check is skipped and the note says "sill height
 *       unknown" with the reason — never a guess.
 *   Radiator free-standing: whether it runs into a wall line.
 *   Radiator or outdoor unit: whether its footprint overlaps another's.
 *   Outdoor unit: whether it crosses a wall line, or sits inside the walls.
 *   Clearances round an outdoor unit are NOT checked here — they are each
 *   manufacturer's, and the MCS design check (`design-check-outdoor-unit.ts`)
 *   checks them against the wall the unit stands at, from the manufacturer's
 *   own figures where those are sourced and the designer's otherwise.
 *
 * WHERE THIS LIVES. Moved from the portal to @sts/calc on 2026-09-10 so the
 * same checks run in the API's design check and, later, the installer pack.
 * The portal's `plan-fit.ts` re-exports this module unchanged.
 *
 * THRESHOLDS. `FIT_TOLERANCE_M` (half a millimetre) is the only slack: a
 * radiator exactly as long as the clear run fits, one a millimetre longer does
 * not. The scan's own accuracy is a few centimetres; the checks do not pretend
 * otherwise — they are ADVISORY, never block a save, and say what they
 * compared so the designer can overrule them with what they know from site.
 *
 * WHAT A WALL IS HERE. RoomPlan's wall line is the room-facing surface, so the
 * clear run is plaster to plaster with no allowance for skirting, valves or
 * pipe tails, and an outdoor unit whose back touches a wall line is taken as
 * outside even though the wall's real thickness is not in the scan.
 */

import type { PlanFloorLevel, PlanPoint, PlanRoom, PlanSegment } from "@sts/geometry";
import {
  RADIATOR_PLAN_RANGE,
  radiatorHeights,
  radiatorLabel,
  radiatorLengths,
  radiatorMountingHeight,
  radiatorSpecFor,
  type PlacedGeometry,
  type RadiatorSpec,
} from "@sts/rules-data";
import {
  PLAN_OBJECT_KIND_NAMES as KIND_NAMES,
  axesOf,
  footprintM,
  footprintedCaption as objectCaption,
  placedCorners,
  wallsFacingRooms,
  type PlanObjectOnPlan as PlanObject,
  type PlanWall,
} from "./plan-geometry.js";

type Point = PlanPoint;
type Radiator = Extract<PlanObject, { kind: "radiator" }>;
type Footprinted = Extract<PlanObject, { kind: "radiator" | "heat-pump" }>;

// ----------------------------------------------------------- thresholds ----

/** The one slack in every comparison: half a millimetre. Exact fits pass; 1 mm over fails. */
export const FIT_TOLERANCE_M = 0.0005;
/** A radiator within 5° of a wall's direction runs along it. */
const PARALLEL_SIN = Math.sin((5 * Math.PI) / 180);
/** Pieces of one wall RoomPlan split in two: within 2°, 30 mm of the line and 50 mm end to end. */
const RUN_PARALLEL_SIN = Math.sin((2 * Math.PI) / 180);
const RUN_OFFSET_M = 0.03;
const RUN_JOIN_M = 0.05;
/**
 * An opening this far off the wall line still counts as in it — a door
 * recorded on the other room's face of a party wall. About the thickest
 * domestic wall.
 */
const OPENING_REACH_M = 0.4;
/** Footprints must interpenetrate by more than this to overlap: touching is fine. */
const OVERLAP_M = 0.001;
/** A footprint is shrunk by this before testing it against wall lines, so one flush against a wall passes. */
const WALL_TOUCH_M = 0.005;

// ---------------------------------------------------------------- types ----

export type FitProblemKind =
  | "too-long"
  | "overhangs-corner"
  | "blocks-opening"
  | "above-sill"
  | "crosses-wall"
  | "in-building"
  | "overlaps";

export interface FitProblem {
  readonly kind: FitProblemKind;
  /** One sentence, with the numbers it compared. */
  readonly message: string;
  /** A few words for the tag on the drawing: "Too long: 1.05 m clear". */
  readonly short: string;
  /** The opening, wall or object it is about. */
  readonly otherId?: string;
}

export type FitNoteKind = "sill-unknown" | "sill-clear" | "free-standing" | "clearances";

/** Something worth knowing that is not a problem. */
export interface FitNote {
  readonly kind: FitNoteKind;
  readonly message: string;
}

/** A change that the same checks say fixes every problem. */
export interface FitSuggestion {
  /** "Use K2 600×1000 — fits" */
  readonly label: string;
  readonly spec: RadiatorSpec;
  readonly geometry: PlacedGeometry;
}

export interface ObjectFit {
  readonly id: string;
  readonly problems: readonly FitProblem[];
  readonly notes: readonly FitNote[];
  readonly suggestions: readonly FitSuggestion[];
  /** Set when a size change would be the fix and the range has no size that fixes it. */
  readonly noSuggestion?: string;
  /** For a radiator on a wall: the clear run where it sits, metres. */
  readonly clearRunM?: number;
}

/** The scan, prepared once per storey for every check. */
export interface FitScene {
  readonly walls: readonly PlanWall[];
  readonly wallSegments: ReadonlyMap<string, PlanSegment>;
  readonly openings: readonly (PlanSegment & { readonly floorLevel: PlanFloorLevel })[];
  readonly rooms: readonly PlanRoom[];
}

export function buildFitScene(rooms: readonly PlanRoom[]): FitScene {
  const wallSegments = new Map<string, PlanSegment>();
  const openings: (PlanSegment & { floorLevel: PlanFloorLevel })[] = [];
  for (const room of rooms) {
    for (const segment of room.segments) {
      if (segment.kind === "wall") wallSegments.set(segment.id, segment);
      else if (segment.kind === "window" || segment.kind === "door" || segment.kind === "opening") {
        openings.push({ ...segment, floorLevel: room.floorLevel });
      }
    }
  }
  return { walls: wallsFacingRooms(rooms), wallSegments, openings, rooms };
}

// ------------------------------------------------------------ vectors ----

const sub = (a: Point, b: Point): Point => ({ x: a.x - b.x, y: a.y - b.y });
const dot = (a: Point, b: Point): number => a.x * b.x + a.y * b.y;
const cross = (a: Point, b: Point): number => a.x * b.y - a.y * b.x;

/** "1.05 m" */
export function metres(value: number, decimals = 2): string {
  return `${value.toFixed(decimals)} m`;
}

/** Two figures to as many decimals (2 or 3) as it takes for them not to print alike. */
function metresPair(a: number, b: number): [string, string] {
  const two = [metres(a), metres(b)] as [string, string];
  return two[0] === two[1] && Math.abs(a - b) > FIT_TOLERANCE_M ? [metres(a, 3), metres(b, 3)] : two;
}

// ------------------------------------------------------- the wall seat ----

/** A straight run of wall: one wall, or collinear pieces of one. Distances along `dir` from `origin`. */
export interface WallRun {
  readonly origin: Point;
  readonly dir: Point;
  /** Into the room. */
  readonly inward: Point;
  readonly lo: number;
  readonly hi: number;
  readonly wallIds: ReadonlySet<string>;
}

/** Where a radiator sits on a run. */
interface Seat {
  readonly run: WallRun;
  /** Its centre, along the run. */
  readonly t: number;
  /** Its centre's distance from the wall line, into the room. */
  readonly side: number;
}

export interface RunInterval {
  readonly a: number;
  readonly b: number;
}

export function wallRunOf(host: PlanWall, walls: readonly PlanWall[]): WallRun {
  const d = sub(host.end, host.start);
  const length = Math.hypot(d.x, d.y);
  const dir = { x: d.x / length, y: d.y / length };
  let lo = 0;
  let hi = length;
  const ids = new Set([host.id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const wall of walls) {
      if (ids.has(wall.id)) continue;
      const w = sub(wall.end, wall.start);
      const wl = Math.hypot(w.x, w.y);
      if (!(wl > 1e-6)) continue;
      if (Math.abs(cross(dir, { x: w.x / wl, y: w.y / wl })) > RUN_PARALLEL_SIN) continue;
      if (dot(wall.inward, host.inward) < 0.99) continue;
      const offA = dot(sub(wall.start, host.start), host.inward);
      const offB = dot(sub(wall.end, host.start), host.inward);
      if (Math.abs(offA) > RUN_OFFSET_M || Math.abs(offB) > RUN_OFFSET_M) continue;
      const ta = dot(sub(wall.start, host.start), dir);
      const tb = dot(sub(wall.end, host.start), dir);
      const a = Math.min(ta, tb);
      const b = Math.max(ta, tb);
      if (a > hi + RUN_JOIN_M || b < lo - RUN_JOIN_M) continue;
      lo = Math.min(lo, a);
      hi = Math.max(hi, b);
      ids.add(wall.id);
      grew = true;
    }
  }
  return { origin: host.start, dir, inward: host.inward, lo, hi, wallIds: ids };
}

/**
 * The wall a radiator is against, if any: parallel to it within 5°, on its
 * room side, its back no further from the wall line than its own depth, and
 * alongside some of it. The nearest such wall wins.
 */
function seatOf(geometry: PlacedGeometry, lengthM: number, depthM: number, scene: FitScene): Seat | undefined {
  const { u } = axesOf(geometry.rotationDeg);
  const c = { x: geometry.x, y: geometry.y };
  let best: { wall: PlanWall; score: number } | undefined;
  for (const wall of scene.walls) {
    const d = sub(wall.end, wall.start);
    const length = Math.hypot(d.x, d.y);
    if (!(length > 1e-6)) continue;
    const dir = { x: d.x / length, y: d.y / length };
    if (Math.abs(cross(dir, u)) > PARALLEL_SIN) continue;
    const side = dot(sub(c, wall.start), wall.inward);
    if (side < -0.01) continue;
    const backGap = side - depthM / 2;
    if (backGap > depthM + 0.01) continue;
    const t = dot(sub(c, wall.start), dir);
    if (t + lengthM / 2 <= 0 || t - lengthM / 2 >= length) continue;
    const score = Math.abs(backGap);
    if (!best || score < best.score) best = { wall, score };
  }
  if (!best) return undefined;
  const run = wallRunOf(best.wall, scene.walls);
  return { run, t: dot(sub(c, run.origin), run.dir), side: dot(sub(c, run.origin), run.inward) };
}

/** The openings on a run, as intervals along it. */
export function openingsOnRun(run: WallRun, scene: FitScene): (RunInterval & { readonly segment: FitScene["openings"][number] })[] {
  const found: (RunInterval & { segment: FitScene["openings"][number] })[] = [];
  for (const segment of scene.openings) {
    const d = sub(segment.end, segment.start);
    const length = Math.hypot(d.x, d.y);
    if (!(length > 1e-6)) continue;
    const hosted = segment.parentId !== undefined && run.wallIds.has(segment.parentId);
    if (!hosted) {
      if (Math.abs(cross(run.dir, { x: d.x / length, y: d.y / length })) > PARALLEL_SIN) continue;
      const offA = dot(sub(segment.start, run.origin), run.inward);
      const offB = dot(sub(segment.end, run.origin), run.inward);
      if (Math.abs(offA) > OPENING_REACH_M || Math.abs(offB) > OPENING_REACH_M) continue;
    }
    const ta = dot(sub(segment.start, run.origin), run.dir);
    const tb = dot(sub(segment.end, run.origin), run.dir);
    const a = Math.min(ta, tb);
    const b = Math.max(ta, tb);
    if (b <= run.lo || a >= run.hi) continue;
    found.push({ a, b, segment });
  }
  return found;
}

export interface RunGap extends RunInterval {
  /** What bounds each end: the wall's end, or an opening's label. */
  readonly from: string | null;
  readonly to: string | null;
}

/** The run less its doors and openings. */
export function clearGapsOf(run: WallRun, blockers: readonly (RunInterval & { segment: PlanSegment })[]): RunGap[] {
  const sorted = [...blockers].sort((p, q) => p.a - q.a);
  const gaps: RunGap[] = [];
  let cursor = run.lo;
  let from: string | null = null;
  for (const blocker of sorted) {
    const a = Math.max(blocker.a, run.lo);
    const b = Math.min(blocker.b, run.hi);
    if (a > cursor) gaps.push({ a: cursor, b: a, from, to: blocker.segment.label });
    if (b >= cursor) {
      cursor = b;
      from = blocker.segment.label;
    }
  }
  if (cursor < run.hi) gaps.push({ a: cursor, b: run.hi, from, to: null });
  return gaps;
}

/** The clear stretch a radiator is in — the one holding its centre, or else the one it most overlaps. */
function gapAt(gaps: readonly RunGap[], t: number, r0: number, r1: number): RunGap | undefined {
  const holding = gaps.find((g) => g.a - FIT_TOLERANCE_M <= t && t <= g.b + FIT_TOLERANCE_M);
  if (holding) return holding;
  let best: RunGap | undefined;
  let bestScore = -Infinity;
  for (const g of gaps) {
    const overlap = Math.min(r1, g.b) - Math.max(r0, g.a);
    const score = overlap > 0 ? overlap : -Math.min(Math.abs(t - g.a), Math.abs(t - g.b));
    if (score > bestScore) {
      bestScore = score;
      best = g;
    }
  }
  return best;
}

// --------------------------------------------------------- footprints ----

function cornersOf(object: Footprinted, insetM = 0): readonly Point[] {
  const size = footprintM(object) as { lengthM: number; depthM: number };
  return placedCorners(
    object.geometry,
    Math.max(size.lengthM - 2 * insetM, 0),
    Math.max(size.depthM - 2 * insetM, 0),
  );
}

/** How far two convex footprints interpenetrate (separating axes); ≤ 0 when they do not. */
export function penetration(a: readonly Point[], b: readonly Point[]): number {
  let least = Infinity;
  for (const poly of [a, b]) {
    for (let i = 0; i < poly.length; i += 1) {
      const p = poly[i] as Point;
      const q = poly[(i + 1) % poly.length] as Point;
      const e = sub(q, p);
      const len = Math.hypot(e.x, e.y);
      if (!(len > 1e-9)) continue;
      const axis = { x: -e.y / len, y: e.x / len };
      const pa = a.map((c) => dot(c, axis));
      const pb = b.map((c) => dot(c, axis));
      const overlap = Math.min(Math.max(...pa), Math.max(...pb)) - Math.max(Math.min(...pa), Math.min(...pb));
      if (overlap < least) least = overlap;
    }
  }
  return least;
}

export function insideConvex(point: Point, poly: readonly Point[]): boolean {
  let sign = 0;
  for (let i = 0; i < poly.length; i += 1) {
    const p = poly[i] as Point;
    const q = poly[(i + 1) % poly.length] as Point;
    const c = cross(sub(q, p), sub(point, p));
    if (Math.abs(c) < 1e-12) return false;
    const s = Math.sign(c);
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

function segmentsCross(a: Point, b: Point, c: Point, d: Point): boolean {
  const d1 = cross(sub(b, a), sub(c, a));
  const d2 = cross(sub(b, a), sub(d, a));
  const d3 = cross(sub(d, c), sub(a, c));
  const d4 = cross(sub(d, c), sub(b, c));
  return d1 * d2 < 0 && d3 * d4 < 0;
}

export function segmentHitsConvex(a: Point, b: Point, poly: readonly Point[]): boolean {
  if (insideConvex(a, poly) || insideConvex(b, poly)) return true;
  for (let i = 0; i < poly.length; i += 1) {
    if (segmentsCross(a, b, poly[i] as Point, poly[(i + 1) % poly.length] as Point)) return true;
  }
  return false;
}

/** The first wall line a footprint runs across, if any. */
function wallCrossed(object: Footprinted, scene: FitScene): PlanSegment | undefined {
  const poly = cornersOf(object, WALL_TOUCH_M);
  for (const wall of scene.wallSegments.values()) {
    if (segmentHitsConvex(wall.start, wall.end, poly)) return wall;
  }
  return undefined;
}

/** Walls on every side: eight rays from the point, every one of them stopped by a wall. */
function enclosedByWalls(point: Point, scene: FitScene): boolean {
  if (scene.wallSegments.size < 3) return false;
  const FAR = 1e4;
  for (let k = 0; k < 8; k += 1) {
    const angle = (k * Math.PI) / 4 + 0.1; // off the axes, so a ray never runs exactly along a wall
    const far = { x: point.x + Math.cos(angle) * FAR, y: point.y + Math.sin(angle) * FAR };
    let hit = false;
    for (const wall of scene.wallSegments.values()) {
      if (segmentsCross(point, far, wall.start, wall.end)) {
        hit = true;
        break;
      }
    }
    if (!hit) return false;
  }
  return true;
}

function roomHolding(point: Point, scene: FitScene): PlanRoom | undefined {
  return scene.rooms.find((room) => room.outlines.some((outline) => insideConvex(point, outline)));
}

function overlapProblems(object: Footprinted, others: readonly PlanObject[]): FitProblem[] {
  const mine = cornersOf(object);
  const problems: FitProblem[] = [];
  for (const other of others) {
    if (other.id === object.id || (other.kind !== "radiator" && other.kind !== "heat-pump")) continue;
    if (penetration(mine, cornersOf(other)) > OVERLAP_M) {
      problems.push({
        kind: "overlaps",
        message: `Overlaps the ${KIND_NAMES[other.kind].toLowerCase()} ${objectCaption(other)}.`,
        short: `Overlaps a ${KIND_NAMES[other.kind].toLowerCase()}`,
        otherId: other.id,
      });
    }
  }
  return problems;
}

// ------------------------------------------------------------ radiator ----

interface RadiatorReading {
  readonly problems: FitProblem[];
  readonly notes: FitNote[];
  readonly seat?: Seat;
  readonly gap?: RunGap;
}

const WALL_PROBLEMS: ReadonlySet<FitProblemKind> = new Set([
  "too-long",
  "overhangs-corner",
  "blocks-opening",
  "above-sill",
]);

function readRadiator(radiator: Radiator, others: readonly PlanObject[], scene: FitScene): RadiatorReading {
  const L = radiator.spec.lengthMm / 1000;
  const D = radiator.spec.depthMm / 1000;
  const problems: FitProblem[] = [];
  const notes: FitNote[] = [];
  const seat = seatOf(radiator.geometry, L, D, scene);

  if (!seat) {
    notes.push({
      kind: "free-standing",
      message: "Not against a wall, so the wall, door and window checks do not apply.",
    });
    const crossed = wallCrossed(radiator, scene);
    if (crossed) {
      problems.push({
        kind: "crosses-wall",
        message: `Runs into a wall (${crossed.roomLabel}, ${crossed.label}).`,
        short: "Runs into a wall",
        otherId: crossed.id,
      });
    }
    problems.push(...overlapProblems(radiator, others));
    return { problems, notes };
  }

  const { run, t } = seat;
  const r0 = t - L / 2;
  const r1 = t + L / 2;
  const on = openingsOnRun(run, scene);
  const blockers = on.filter((o) => o.segment.kind !== "window");
  const gaps = clearGapsOf(run, blockers);
  const gap = gapAt(gaps, t, r0, r1);

  for (const blocker of blockers) {
    const across = Math.min(r1, blocker.b) - Math.max(r0, blocker.a);
    if (across > FIT_TOLERANCE_M) {
      const what = blocker.segment.kind === "door" ? "door" : "opening";
      problems.push({
        kind: "blocks-opening",
        message: `Blocks the ${what} (${blocker.segment.label}): ${metres(across)} of the radiator is across it.`,
        short: `Blocks the ${what}`,
        otherId: blocker.segment.id,
      });
    }
  }

  const clear = gap ? gap.b - gap.a : 0;
  if (L > clear + FIT_TOLERANCE_M) {
    const [c, l] = metresPair(clear, L);
    const where =
      !gap
        ? `No clear wall here: the doors and openings take up the whole run, radiator is ${l}.`
        : gap.from === null && gap.to === null
          ? `Too long for this wall: ${c} clear here, radiator is ${l}.`
          : `Too long for this space: ${c} clear here (${gap.from ?? "corner"} to ${gap.to ?? "corner"}), radiator is ${l}.`;
    problems.push({ kind: "too-long", message: where, short: `Too long: ${c} clear` });
  } else {
    const over = Math.max(run.lo - r0, r1 - run.hi);
    if (over > FIT_TOLERANCE_M) {
      problems.push({
        kind: "overhangs-corner",
        message: `Overhangs the corner by ${metres(over)}: slide it along the wall.`,
        short: `Overhangs the corner by ${metres(over)}`,
      });
    }
  }

  const mount = radiatorMountingHeight(radiator.spec);
  const topM = (mount.mm + radiator.spec.heightMm) / 1000;
  for (const window of on.filter((o) => o.segment.kind === "window")) {
    const under = Math.min(r1, window.b) - Math.max(r0, window.a);
    if (!(under > FIT_TOLERANCE_M)) continue;
    const { segment } = window;
    if (!segment.vertical) {
      notes.push({
        kind: "sill-unknown",
        message: `Sill height unknown for ${segment.label}: the scan does not give the window's height.`,
      });
      continue;
    }
    if (!segment.floorLevel.known) {
      notes.push({
        kind: "sill-unknown",
        message: `Sill height unknown for ${segment.label}: ${segment.floorLevel.reason}, so the radiator's height is not checked against it.`,
      });
      continue;
    }
    const sillM = segment.vertical.bottomY - segment.floorLevel.y;
    const hung = `${radiator.spec.heightMm} mm radiator + ${mount.mm} mm off the floor${mount.assumed ? ", assumed" : ""}`;
    if (topM > sillM + FIT_TOLERANCE_M) {
      const [s, top] = metresPair(sillM, topM);
      problems.push({
        kind: "above-sill",
        message: `Too tall for under ${segment.label}: the sill is ${s} above the floor, the radiator's top would be at ${top} (${hung}).`,
        short: `Above the sill of ${segment.label}`,
        otherId: segment.id,
      });
    } else {
      notes.push({
        kind: "sill-clear",
        message: `Under ${segment.label}: clears the sill (${metres(sillM)} above the floor) by ${Math.round((sillM - topM) * 1000)} mm (${hung}).`,
      });
    }
  }

  problems.push(...overlapProblems(radiator, others));
  return { problems, notes, seat, ...(gap ? { gap } : {}) };
}

/** The same radiator at `spec`, its back where it was, centred at `t` along its run. */
function reseated(radiator: Radiator, seat: Seat, spec: RadiatorSpec, t: number): Radiator {
  const back = seat.side - radiator.spec.depthMm / 2000;
  const side = back + spec.depthMm / 2000;
  const { run } = seat;
  return {
    ...radiator,
    spec,
    geometry: {
      x: run.origin.x + run.dir.x * t + run.inward.x * side,
      y: run.origin.y + run.dir.y * t + run.inward.y * side,
      rotationDeg: radiator.geometry.rotationDeg,
    },
  };
}

/** Centred as near `t` as it can be while staying inside `gap`. */
function clampInto(gap: RunInterval, lengthM: number, t: number): number {
  return Math.min(Math.max(t, gap.a + lengthM / 2), gap.b - lengthM / 2);
}

function withMounting(spec: RadiatorSpec, from: RadiatorSpec): RadiatorSpec {
  return from.mountingHeightMm === undefined ? spec : { ...spec, mountingHeightMm: from.mountingHeightMm };
}

function suggestFor(
  radiator: Radiator,
  reading: RadiatorReading,
  others: readonly PlanObject[],
  scene: FitScene,
): { suggestions: FitSuggestion[]; noSuggestion?: string } {
  const kinds = new Set(reading.problems.map((p) => p.kind));
  const { seat, gap } = reading;
  if (!seat || !gap || ![...kinds].some((k) => WALL_PROBLEMS.has(k))) return { suggestions: [] };

  const { panelType, heightMm, lengthMm } = radiator.spec;
  const clear = gap.b - gap.a;
  const fits = (candidate: Radiator): boolean => readRadiator(candidate, others, scene).problems.length === 0;
  /**
   * `spec` somewhere in the clear stretch that passes every check: as near
   * where it is now as the stretch allows, else pushed to whichever end of
   * the stretch is nearer, else the other — so a neighbour at one end does
   * not rule out a size that fits at the other.
   */
  const placed = (spec: RadiatorSpec): Radiator | undefined => {
    const L = spec.lengthMm / 1000;
    const spots = [clampInto(gap, L, seat.t), gap.a + L / 2, gap.b - L / 2].sort(
      (p, q) => Math.abs(p - seat.t) - Math.abs(q - seat.t),
    );
    for (const t of spots) {
      const candidate = reseated(radiator, seat, spec, t);
      if (fits(candidate)) return candidate;
    }
    return undefined;
  };
  const suggestions: FitSuggestion[] = [];
  const offer = (label: string, candidate: Radiator): void => {
    suggestions.push({ label, spec: candidate.spec, geometry: candidate.geometry });
  };

  // 1. The same radiator, slid clear — when it is as short as the gap.
  let slid = false;
  if ((kinds.has("overhangs-corner") || kinds.has("blocks-opening")) && lengthMm / 1000 <= clear + FIT_TOLERANCE_M) {
    const moved = placed(radiator.spec);
    if (moved) {
      const by = Math.hypot(moved.geometry.x - radiator.geometry.x, moved.geometry.y - radiator.geometry.y);
      offer(`Move it ${metres(by)} along — fits`, moved);
      slid = true;
    }
  }

  // 2. The longest length of the same type and height that fits the clear run.
  if (!slid && (kinds.has("too-long") || kinds.has("blocks-opening"))) {
    const shorter = radiatorLengths(panelType, heightMm)
      .filter((l) => l < lengthMm && l / 1000 <= clear + FIT_TOLERANCE_M)
      .sort((p, q) => q - p);
    for (const length of shorter) {
      const spec = radiatorSpecFor(panelType, heightMm, length);
      if (!spec) continue;
      const candidate = placed(withMounting(spec, radiator.spec));
      if (candidate) {
        offer(`Use ${radiatorLabel(spec)} — fits`, candidate);
        break;
      }
    }
  }

  // 3. A lower height of the same type — for a sill, or when no length alone
  //    would do — at the length nearest the current one that fits.
  if (kinds.has("above-sill") || suggestions.length === 0) {
    const target = Math.min(lengthMm, Math.floor((clear + FIT_TOLERANCE_M) * 1000));
    search: for (const height of radiatorHeights(panelType).filter((h) => h < heightMm).sort((p, q) => q - p)) {
      const lengths = radiatorLengths(panelType, height)
        .filter((l) => l / 1000 <= clear + FIT_TOLERANCE_M)
        .sort((p, q) => Math.abs(p - target) - Math.abs(q - target) || p - q);
      for (const length of lengths) {
        const spec = radiatorSpecFor(panelType, height, length);
        if (!spec) continue;
        const candidate = placed(withMounting(spec, radiator.spec));
        if (candidate) {
          if (!suggestions.some((s) => radiatorLabel(s.spec) === radiatorLabel(spec))) {
            offer(`Use ${radiatorLabel(spec)} — fits`, candidate);
          }
          break search;
        }
      }
    }
  }

  return suggestions.length > 0
    ? { suggestions }
    : {
        suggestions,
        noSuggestion: `No ${panelType} size in the ${RADIATOR_PLAN_RANGE.brand} ${RADIATOR_PLAN_RANGE.range} range fits this space — try another wall or another type.`,
      };
}

/**
 * Where a radiator of `spec` could go in place of `radiator` and pass every
 * fit check — the same search the size suggestions above use: its back where
 * it was, as near its present place along the wall as the clear stretch it is
 * in allows, else at either end of that stretch. Free-standing, only its
 * present place is tried. Undefined when nowhere in that stretch will do.
 *
 * Used by the MCS design check to offer a bigger radiator only where one
 * actually fits.
 */
export function fittingPlacement(
  radiator: Radiator,
  spec: RadiatorSpec,
  others: readonly PlanObject[],
  scene: FitScene,
): PlacedGeometry | undefined {
  const next = withMounting(spec, radiator.spec);
  const fits = (candidate: Radiator): boolean => readRadiator(candidate, others, scene).problems.length === 0;
  const { seat, gap } = readRadiator(radiator, others, scene);
  if (!seat) {
    return fits({ ...radiator, spec: next }) ? radiator.geometry : undefined;
  }
  if (!gap) return undefined;
  const L = next.lengthMm / 1000;
  const spots = [clampInto(gap, L, seat.t), gap.a + L / 2, gap.b - L / 2].sort(
    (p, q) => Math.abs(p - seat.t) - Math.abs(q - seat.t),
  );
  for (const t of spots) {
    const candidate = reseated(radiator, seat, next, t);
    if (fits(candidate)) return candidate.geometry;
  }
  return undefined;
}

// ---------------------------------------------------------- heat pump ----

function readHeatPump(unit: Extract<PlanObject, { kind: "heat-pump" }>, others: readonly PlanObject[], scene: FitScene): FitProblem[] {
  const problems: FitProblem[] = [];
  const crossed = wallCrossed(unit, scene);
  const centre = { x: unit.geometry.x, y: unit.geometry.y };
  if (crossed) {
    problems.push({
      kind: "crosses-wall",
      message: `Across a wall (${crossed.roomLabel}, ${crossed.label}): an outdoor unit goes outside the building.`,
      short: "Across a wall",
      otherId: crossed.id,
    });
  } else if (enclosedByWalls(centre, scene)) {
    const room = roomHolding(centre, scene);
    problems.push({
      kind: "in-building",
      message: `Inside the building${room ? ` (${room.label})` : ""}: an outdoor unit goes outside.`,
      short: "Inside the building",
    });
  }
  problems.push(...overlapProblems(unit, others));
  return problems;
}

const CLEARANCES_NOTE: FitNote = {
  kind: "clearances",
  message:
    "Clearances round the unit are not part of this fit check: they are the manufacturer's, and the MCS design check tests them against this wall where the manual's figures are known.",
};

// -------------------------------------------------------------- entry ----

/**
 * One object's fit, among `objects` (the same storey's, the object itself
 * among them or not). Undefined for pipes and fittings, which have no checks.
 * `suggest: false` skips the size search — for the ghost under the cursor.
 */
export function fitOf(
  object: PlanObject,
  objects: readonly PlanObject[],
  scene: FitScene,
  options: { readonly suggest?: boolean } = {},
): ObjectFit | undefined {
  if (object.kind === "heat-pump") {
    return { id: object.id, problems: readHeatPump(object, objects, scene), notes: [CLEARANCES_NOTE], suggestions: [] };
  }
  if (object.kind !== "radiator") return undefined;
  const reading = readRadiator(object, objects, scene);
  const found = options.suggest === false ? { suggestions: [] } : suggestFor(object, reading, objects, scene);
  return {
    id: object.id,
    problems: reading.problems,
    notes: reading.notes,
    suggestions: found.suggestions,
    ...("noSuggestion" in found && found.noSuggestion ? { noSuggestion: found.noSuggestion } : {}),
    ...(reading.gap ? { clearRunM: reading.gap.b - reading.gap.a } : {}),
  };
}

/**
 * Every radiator's and outdoor unit's fit on one storey, by object id.
 *
 * `suggestFor` limits the size search to those ids — the plan passes the
 * selected object, the only one whose suggestions are on screen, so a drag
 * re-checks everything on every move without re-running the search for
 * every flagged radiator. Omitted, every object gets its suggestions.
 */
export function checkPlanFit(
  objects: readonly PlanObject[],
  rooms: readonly PlanRoom[],
  scene: FitScene = buildFitScene(rooms),
  options: { readonly suggestFor?: ReadonlySet<string> } = {},
): ReadonlyMap<string, ObjectFit> {
  const fits = new Map<string, ObjectFit>();
  for (const object of objects) {
    const suggest = options.suggestFor === undefined || options.suggestFor.has(object.id);
    const fit = fitOf(object, objects, scene, { suggest });
    if (fit) fits.set(object.id, fit);
  }
  return fits;
}

/**
 * A radiator changed to `spec` in place: its back stays where it was (so a
 * deeper panel type grows into the room, not into the wall) and its centre
 * stays where it was along the wall. Free-standing, it keeps its centre.
 */
export function resizedRadiatorGeometry(radiator: Radiator, spec: RadiatorSpec, scene: FitScene): PlacedGeometry {
  const seat = seatOf(
    radiator.geometry,
    radiator.spec.lengthMm / 1000,
    radiator.spec.depthMm / 1000,
    scene,
  );
  return seat ? reseated(radiator, seat, spec, seat.t).geometry : radiator.geometry;
}

/** A placed object and whether it fits — for anything that lists the plan's objects. */
export interface ObjectFitStatus {
  readonly id: string;
  readonly kind: "radiator" | "heat-pump";
  readonly storey: number;
  readonly caption: string;
  readonly fits: boolean;
  readonly problems: readonly string[];
  readonly notes: readonly string[];
}

/**
 * Every radiator and outdoor unit on the plan, every storey, with its fit —
 * each storey checked against that storey's rooms only.
 */
export function listFitStatus(
  objects: readonly PlanObject[],
  rooms: readonly PlanRoom[],
): readonly ObjectFitStatus[] {
  const byStorey = new Map<number, PlanObject[]>();
  for (const object of objects) {
    const list = byStorey.get(object.storey) ?? [];
    list.push(object);
    byStorey.set(object.storey, list);
  }
  const out: ObjectFitStatus[] = [];
  for (const [storey, list] of byStorey) {
    const scene = buildFitScene(rooms.filter((room) => room.story === storey));
    for (const object of list) {
      if (object.kind !== "radiator" && object.kind !== "heat-pump") continue;
      const fit = fitOf(object, list, scene, { suggest: false });
      if (!fit) continue;
      out.push({
        id: object.id,
        kind: object.kind,
        storey,
        caption: objectCaption(object),
        fits: fit.problems.length === 0,
        problems: fit.problems.map((p) => p.message),
        notes: fit.notes.map((n) => n.message),
      });
    }
  }
  return out;
}
